/** @jsxImportSource @emotion/react */
import { ColDef, ICellRendererParams, IsRowMaster } from "ag-grid-community";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import assert from "assert";
import { useCallback, useMemo } from "react";
import {
  DraftSpecParam,
  ParamsEditorRowDatum,
} from "components/ToolEditor/ToolEditor.types";

import ToolPathSingleParamTypeV3Schema from "types/json-schemas/ToolPathSingleParamTypeV3.json";
import ToolIntParamTypeV3Schema from "types/json-schemas/ToolIntParamTypeV3.json";
import ToolStringParamTypeV3Schema from "types/json-schemas/ToolStringParamTypeV3.json";
import ToolFloatParamTypeV3Schema from "types/json-schemas/ToolFloatParamTypeV3.json";
import ToolChoiceParamTypeV3Schema from "types/json-schemas/ToolChoiceParamTypeV3.json";
import ToolBooleanParamTypeV3Schema from "types/json-schemas/ToolBooleanParamTypeV3.json";
import ToolRoiFrameParamTypeV3Schema from "types/json-schemas/ToolRoiFrameParamTypeV3.json";
import ToolCellStatusParamTypeV3Schema from "types/json-schemas/ToolCellStatusParamTypeV3.json";
import { ParameterViewerDetailRenderer } from "components/ToolEditor/Form/ToolEditorSpecEditor/ParametersEditor/DetailRenderer/ParameterViewerDetailRenderer";

export const TOOL_EDITOR_PARAMETER_PROPERTIES = {
  ToolPathParam: {
    friendlyName: "File",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolPathSingleParamTypeV3Schema.properties,
  } as const,
  ToolStringParam: {
    friendlyName: "String",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolStringParamTypeV3Schema.properties,
  } as const,
  ToolIntRangeParam: {
    friendlyName: "Integer",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolIntParamTypeV3Schema.properties,
  } as const,
  ToolFloatRangeParam: {
    friendlyName: "Float",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolFloatParamTypeV3Schema.properties,
  } as const,
  ToolChoiceParam: {
    friendlyName: "Choice",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolChoiceParamTypeV3Schema.properties,
  } as const,
  ToolBooleanParam: {
    friendlyName: "Boolean",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolBooleanParamTypeV3Schema.properties,
  } as const,
  ToolRoiFrameParam: {
    friendlyName: "Roi",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolRoiFrameParamTypeV3Schema.properties,
  } as const,
  ToolCellStatusParam: {
    friendlyName: "Cell Status",
    isDetailRendererEnabled: true,
    isDefaultEditable: "default" in ToolCellStatusParamTypeV3Schema.properties,
  } as const,
} as const;

interface ToolSpecEditorColDef extends ColDef<ParamsEditorRowDatum> {
  colId: keyof DraftSpecParam | "default" | undefined;
}

const detailCellRendererParams = { style: { backgroundColor: "black" } };
const css = { width: "100%", minWidth: 800 };
interface ParametersEditorProps {
  rowData: ParamsEditorRowDatum[];
}

export const ParametersViewer = ({ rowData }: ParametersEditorProps) => {
  const isRowMaster: IsRowMaster<ParamsEditorRowDatum> = useCallback((data) => {
    return TOOL_EDITOR_PARAMETER_PROPERTIES[data.type.param_type]
      .isDetailRendererEnabled;
  }, []);

  const defaultColDef = useMemo(() => {
    const defaultColDef: AgGridReactProps["defaultColDef"] = {
      sortable: false,
      suppressHeaderMenuButton: true,
    };
    return defaultColDef;
  }, []);

  const columnDefs: ToolSpecEditorColDef[] = useMemo(
    () => [
      {
        colId: "key",
        field: "key",
        cellRenderer: "agGroupCellRenderer",
        width: 150,
      },
      {
        colId: "name",
        field: "name",
        width: 150,
        valueGetter: ({ data }) => data?.name,
      },
      {
        colId: "type",
        field: "type",
        width: 75,
        cellRenderer: ({ data }: ICellRendererParams<ParamsEditorRowDatum>) => {
          const paramType = data?.type.param_type;
          assert(paramType !== undefined, "Data undefined in cell renderer");
          return TOOL_EDITOR_PARAMETER_PROPERTIES[paramType].friendlyName;
        },
      },
      {
        colId: "required",
        field: "required",
        width: 80,
      },
      {
        colId: "default",
        headerName: "default",
        width: 75,
        valueGetter: ({ data }) => {
          if (data === undefined) {
            return null;
          }
          const type = data.type;

          return "default" in type ? type.default : null;
        },
      },
      {
        colId: "description",
        field: "description",
        headerName: "Description",
        flex: 1,
      },
    ],
    [],
  );

  const getRowId = useCallback(
    ({ data }: { data: (typeof rowData)[number] }) => data.key,
    [],
  );

  return (
    <AgGridReact<ParamsEditorRowDatum>
      className="ag-theme-balham-cell-borders"
      css={css}
      domLayout="autoHeight"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={rowData}
      getRowId={getRowId}
      detailCellRenderer={ParameterViewerDetailRenderer}
      isRowMaster={isRowMaster}
      masterDetail={true}
      detailCellRendererParams={detailCellRendererParams}
      detailRowAutoHeight
      reactiveCustomComponents
      readOnlyEdit
    />
  );
};
